import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { requireCompany } from "@/lib/company-access";

// In-app notification center: everything that happened to the company
// recently — new leads, new Exchange matches, new service requests —
// merged into one reverse-chronological feed. Read state lives client-side
// (localStorage last-seen timestamp); the feed itself is plain RLS reads.

export type NotificationItem = {
  id: string;
  kind: "lead" | "match" | "request";
  name: string | null;
  detail: string | null;
  status: string | null;
  ts: string;
};

export const listNotifications = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z.object({ sinceDays: z.number().int().min(1).max(30).optional() }).parse(d ?? {}),
  )
  .middleware([requireSupabaseAuth])
  .handler(async ({ context, data }) => {
    const access = await requireCompany(context.supabase, context.userId);
    const since = new Date(Date.now() - (data.sinceDays ?? 7) * 86_400_000).toISOString();

    const [leadsRes, matchesRes, requestsRes] = await Promise.all([
      context.supabase
        .from("leads")
        .select("id, name, email, status, created_at")
        .eq("company_id", access.id)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(15),
      context.supabase
        .from("listing_matches")
        .select("id, reason, status, created_at, company_a, company_b")
        .or(`company_a.eq.${access.id},company_b.eq.${access.id}`)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(15),
      context.supabase
        .from("service_requests")
        .select("id, customer_name, category, status, created_at")
        .eq("company_id", access.id)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(15),
    ]);

    const items: NotificationItem[] = [
      ...(leadsRes.data ?? []).map((l) => ({
        id: `lead:${l.id}`,
        kind: "lead" as const,
        name: l.name,
        detail: l.email,
        status: l.status,
        ts: l.created_at,
      })),
      ...(matchesRes.data ?? []).map((m) => ({
        id: `match:${m.id}`,
        kind: "match" as const,
        name: null,
        detail: m.reason ? String(m.reason).slice(0, 140) : null,
        status: m.status,
        ts: m.created_at,
      })),
      ...(requestsRes.data ?? []).map((r) => ({
        id: `request:${r.id}`,
        kind: "request" as const,
        name: r.customer_name,
        detail: r.category,
        status: r.status,
        ts: r.created_at,
      })),
    ]
      .sort((a, b) => (a.ts < b.ts ? 1 : -1))
      .slice(0, 25);

    return { items };
  });
